import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, TextField, Typography, Box, Paper, alpha, useTheme,
  Autocomplete, MenuItem, FormControl, InputLabel, Select, InputAdornment,
  Radio, RadioGroup, FormControlLabel, Divider, Chip, Alert,
  CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import { Close, DeleteSweep } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import feeConcessionService, { ConcessionMode, ConcessionAppliesTo } from '../../services/feeConcessionService';
import feeAssignmentService from '../../services/feeAssignmentService';
import feeCategoryService from '../../services/feeCategoryService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { useAuth } from '../../context/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import { FeeAssignmentRow, FeeCategory } from '../../types/fees';
import { Session, SessionClass } from '../../types/sessions';
import SearchableSelect from '../common/SearchableSelect';
import { buildSessionOptions } from '../../utils/sessionOptions';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * ConcessionFormDialog — create a recurring concession for one or many students
 * in a single action. Fee-assignments are the student search surface because
 * every student on the fee rolls has one (populated with student + class +
 * section). Two entry paths coexist: pick individuals via search, or add every
 * active student in a class in one shot. Selection cloud reconciles both.
 *
 * The backend enforces bulk semantics — one indexed $in for student validation,
 * one insertMany for the write. FE never loops.
 */
const ConcessionFormDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { currency } = useAuth();

  // Pickers
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [categories, setCategories] = useState<FeeCategory[]>([]);

  // Individual student search
  const [studentQuery, setStudentQuery] = useState('');
  const debouncedQuery = useDebounce(studentQuery, 250);
  const [studentOptions, setStudentOptions] = useState<FeeAssignmentRow[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Selection — the source of truth for who this concession applies to.
  // Map for O(1) dedupe on repeated additions from both entry paths.
  const [selectedById, setSelectedById] = useState<Map<string, FeeAssignmentRow>>(new Map());
  const selectedStudents = useMemo(() => Array.from(selectedById.values()), [selectedById]);

  // Class bulk-add
  const [bulkClassId, setBulkClassId] = useState('');
  const [bulkAdding, setBulkAdding] = useState(false);

  // Concession fields
  const [label, setLabel] = useState('');
  const [mode, setMode] = useState<ConcessionMode>('percent');
  const [amount, setAmount] = useState<string>('');
  const [appliesTo, setAppliesTo] = useState<ConcessionAppliesTo>('all');
  const [feeCategoryIds, setFeeCategoryIds] = useState<string[]>([]);
  const [validFrom, setValidFrom] = useState<string>(() => format(new Date(), 'yyyy-MM-dd'));
  const [validTo, setValidTo] = useState<string>('');
  const [reason, setReason] = useState('');

  const [saving, setSaving] = useState(false);

  // ─── Boot / reset ───────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [ss, cls, cats] = await Promise.all([
          sessionsApi.list({ includeArchived: false }),
          classesApi.list(),
          feeCategoryService.getFeeCategories(),
        ]);
        if (cancelled) return;
        setSessions(ss);
        setClasses(cls);
        setCategories(cats);
        const active = ss.find(s => s.status === 'active') ?? ss[0];
        if (active) setSessionId(active._id);
      } catch {
        if (!cancelled) enqueueSnackbar('Failed to load pickers', { variant: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, [open, enqueueSnackbar]);

  useEffect(() => {
    if (!open) return;
    setStudentQuery('');
    setSelectedById(new Map());
    setBulkClassId('');
    setLabel(''); setMode('percent'); setAmount('');
    setAppliesTo('all'); setFeeCategoryIds([]);
    setValidFrom(format(new Date(), 'yyyy-MM-dd')); setValidTo('');
    setReason('');
  }, [open]);

  // Changing session invalidates whatever we picked, since class + roll +
  // section context are session-scoped.
  useEffect(() => {
    if (!open) return;
    setSelectedById(new Map());
    setBulkClassId('');
  }, [sessionId, open]);

  // ─── Student search options ─────────────────────────────────────────────

  useEffect(() => {
    if (!open || !sessionId) { setStudentOptions([]); return; }
    let cancelled = false;
    setLoadingStudents(true);
    (async () => {
      try {
        const res = await feeAssignmentService.listFeeAssignments({
          sessionId,
          search: debouncedQuery || undefined,
          status: 'active',
          limit: 25,
        });
        if (!cancelled) setStudentOptions(res.assignments);
      } catch {
        if (!cancelled) setStudentOptions([]);
      } finally {
        if (!cancelled) setLoadingStudents(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId, debouncedQuery]);

  // Filter out already-selected students so the dropdown never shows a duplicate.
  const availableOptions = useMemo(
    () => studentOptions.filter(o => !selectedById.has(o.studentId)),
    [studentOptions, selectedById],
  );

  // ─── Selection helpers ──────────────────────────────────────────────────

  const addStudent = (row: FeeAssignmentRow) => {
    if (!row) return;
    setSelectedById(prev => {
      if (prev.has(row.studentId)) return prev;
      const next = new Map(prev);
      next.set(row.studentId, row);
      return next;
    });
  };

  const removeStudent = (studentId: string) => {
    setSelectedById(prev => {
      if (!prev.has(studentId)) return prev;
      const next = new Map(prev);
      next.delete(studentId);
      return next;
    });
  };

  const clearAll = () => setSelectedById(new Map());

  const addWholeClass = async () => {
    if (!sessionId || !bulkClassId) return;
    setBulkAdding(true);
    try {
      const res = await feeAssignmentService.listFeeAssignments({
        sessionId,
        classId: bulkClassId,
        status: 'active',
        limit: 200,
      });
      const beforeCount = selectedById.size;
      setSelectedById(prev => {
        const next = new Map(prev);
        for (const r of res.assignments) {
          if (!next.has(r.studentId)) next.set(r.studentId, r);
        }
        return next;
      });
      const addedCount = res.assignments.filter(r => !selectedById.has(r.studentId)).length;
      const className = classes.find(c => c._id === bulkClassId)?.name ?? 'class';
      if (addedCount === 0) {
        enqueueSnackbar(`No new students to add from ${className} — already selected.`, { variant: 'info' });
      } else {
        enqueueSnackbar(`Added ${addedCount} student${addedCount === 1 ? '' : 's'} from ${className}.`, { variant: 'success' });
      }
      // If the class has more than 200 students, surface it so the admin can chase.
      if (res.pagination.totalItems > 200 && beforeCount + addedCount < res.pagination.totalItems) {
        enqueueSnackbar(
          `${className} has ${res.pagination.totalItems} students. Only the first 200 were added — add the rest by searching.`,
          { variant: 'warning' },
        );
      }
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to load class', { variant: 'error' });
    } finally {
      setBulkAdding(false);
    }
  };

  // ─── Derived ────────────────────────────────────────────────────────────

  const isPercent = mode === 'percent';
  const parsedAmount = Number(amount);
  const studentCount = selectedById.size;

  const canSave = useMemo(() => {
    if (studentCount === 0) return false;
    if (!label.trim()) return false;
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return false;
    if (isPercent && parsedAmount > 100) return false;
    if (!validFrom) return false;
    if (appliesTo === 'categories' && feeCategoryIds.length === 0) return false;
    return true;
  }, [studentCount, label, parsedAmount, isPercent, validFrom, appliesTo, feeCategoryIds]);

  // Live explanation of how the current mode + scope + amount will hit an
  // invoice. Percent is per-line; flat is invoice-level split across scope.
  const effectText = useMemo(() => {
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return null;
    const scopeText = appliesTo === 'all'
      ? 'every fee category on the challan'
      : 'the selected categories';
    const scopeFallback = appliesTo === 'categories'
      ? ' If none of the selected categories is on a given challan, nothing is deducted that period.'
      : '';
    if (isPercent) {
      return `Each line covering ${scopeText} is reduced by ${parsedAmount}% of its gross. Multi-line challans sum to ${parsedAmount}% of the bill.${scopeFallback}`;
    }
    const amtLabel = `${currency ?? ''} ${parsedAmount.toLocaleString()}`.trim();
    return `The challan total is reduced by ${amtLabel}, split proportionally across ${scopeText}. The concession never exceeds what those lines actually total on the bill.${scopeFallback}`;
  }, [parsedAmount, isPercent, appliesTo, currency]);

  const submitLabel = useMemo(() => {
    if (saving) return 'Saving…';
    if (studentCount === 0) return 'Create concession';
    return `Create · ${studentCount} student${studentCount === 1 ? '' : 's'}`;
  }, [saving, studentCount]);

  // ─── Save ───────────────────────────────────────────────────────────────

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const studentIds = Array.from(selectedById.keys());
      const result = await feeConcessionService.createBulkConcession({
        studentIds,
        label: label.trim(),
        mode,
        amount: Math.round(parsedAmount * 100) / 100,
        appliesTo,
        feeCategoryIds: appliesTo === 'categories' ? feeCategoryIds : undefined,
        validFrom,
        validTo: validTo || null,
        reason: reason.trim() || undefined,
      });
      const skippedTail = result.skipped > 0 ? ` (${result.skipped} skipped)` : '';
      enqueueSnackbar(
        `${result.created} concession${result.created === 1 ? '' : 's'} ${result.pending ? 'sent for approval' : 'created'}${skippedTail}`,
        { variant: 'success' },
      );
      onCreated();
      onClose();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to create concession', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  // Divider colour is already alpha-loaded by MUI. Reusing it directly gives a
  // soft, on-brand hairline; passing it through `alpha()` overrides that alpha
  // and produces a jarring near-black outline.
  const softBorder = theme.palette.divider;

  const surface = (children: React.ReactNode) => (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2.5,
        borderColor: softBorder,
        bgcolor: theme.palette.background.paper,
      }}
    >
      {children}
    </Paper>
  );

  const sectionHeader = (
    title: string,
    rightSlot?: React.ReactNode,
  ) => (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ letterSpacing: 0.2, flex: 1 }}>
        {title}
      </Typography>
      {rightSlot}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' },
      }}
    >
      <DialogTitle component="div" sx={{ pb: 1.25, pt: 2.5, px: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              New concession
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Recurring reduction on future challans for one or more students. Existing challans are unchanged — use a Waiver for those.
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={{ color: 'text.secondary' }}
            aria-label="Close"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 2, bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
        <Stack spacing={2.5}>

          {/* ─── Students ─────────────────────────────────────────────── */}
          {surface(
            <>
              {sectionHeader(
                'Students',
                <Chip
                  size="small"
                  label={`${studentCount} selected`}
                  sx={{
                    height: 22, fontWeight: 700, fontSize: '0.7rem',
                    bgcolor: studentCount > 0
                      ? alpha(theme.palette.primary.main, 0.12)
                      : alpha(theme.palette.text.primary, 0.06),
                    color: studentCount > 0
                      ? theme.palette.primary.dark
                      : 'text.secondary',
                  }}
                />
              )}

              <Stack spacing={1.75}>
                <SearchableSelect
                  label="Session"
                  value={sessionId}
                  onChange={v => setSessionId(v)}
                  options={buildSessionOptions(sessions, theme)}
                  clearable={false}
                  minWidth={220}
                />

                {/* Individual search */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  Add by search
                </Typography>
                <Autocomplete
                  options={availableOptions}
                  loading={loadingStudents}
                  value={null}
                  blurOnSelect
                  clearOnBlur
                  onChange={(_, v) => { if (v) { addStudent(v); setStudentQuery(''); } }}
                  onInputChange={(_, v, reason) => { if (reason !== 'reset') setStudentQuery(v); }}
                  inputValue={studentQuery}
                  getOptionLabel={(o) => o.student?.name ?? ''}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  filterOptions={(opts) => opts}   // server-side search
                  noOptionsText={sessionId ? 'Type a name or GR number to search…' : 'Pick a session first'}
                  renderOption={(props, o) => (
                    <li {...props} key={o._id}>
                      <Stack spacing={0.25}>
                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography variant="body2" fontWeight={600}>{o.student.name}</Typography>
                          {o.student.grNumber && (
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>
                              {o.student.grNumber}
                            </Typography>
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {o.class.name ?? '—'}
                          {o.section?.name ? ` · ${o.section.name}` : ''}
                          {o.rollNumber != null ? ` · Roll ${o.rollNumber}` : ''}
                        </Typography>
                      </Stack>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Type a student's name…"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingStudents ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {/* Class bulk-add */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mt: 0.5, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  Or add a whole class
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <FormControl size="small" sx={{ flex: 1 }} disabled={!sessionId || bulkAdding}>
                    <InputLabel>Class</InputLabel>
                    <Select
                      label="Class"
                      value={bulkClassId}
                      onChange={e => setBulkClassId(e.target.value)}
                    >
                      <MenuItem value=""><em>Select a class…</em></MenuItem>
                      {classes.map(c => (
                        <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    onClick={addWholeClass}
                    disabled={!sessionId || !bulkClassId || bulkAdding}
                    sx={{ borderRadius: 1.5, minWidth: 180, borderColor: softBorder }}
                    startIcon={bulkAdding ? <CircularProgress size={14} /> : null}
                  >
                    {bulkAdding ? 'Adding…' : 'Add all active students'}
                  </Button>
                </Stack>

                {/* Selection cloud */}
                {studentCount > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Divider sx={{ mb: 1.25 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Selected students
                      </Typography>
                      <Button
                        size="small"
                        onClick={clearAll}
                        startIcon={<DeleteSweep fontSize="small" />}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                      >
                        Clear all
                      </Button>
                    </Stack>
                    <Box
                      sx={{
                        display: 'flex', flexWrap: 'wrap', gap: 0.75,
                        maxHeight: 180, overflowY: 'auto',
                        p: 1.25, borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                        border: `1px dashed ${softBorder}`,
                      }}
                    >
                      {selectedStudents.map(s => (
                        <Tooltip
                          key={s.studentId}
                          title={
                            <>
                              {s.student?.grNumber ? `ID ${s.student.grNumber} · ` : ''}
                              {s.class?.name ?? '—'}
                              {s.section?.name ? ` · ${s.section.name}` : ''}
                              {s.rollNumber != null ? ` · Roll ${s.rollNumber}` : ''}
                            </>
                          }
                          arrow
                        >
                          <Chip
                            label={s.student.name}
                            onDelete={() => removeStudent(s.studentId)}
                            size="small"
                            sx={{
                              height: 26, fontWeight: 600, fontSize: '0.75rem',
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: theme.palette.primary.dark,
                              '& .MuiChip-deleteIcon': {
                                color: alpha(theme.palette.primary.main, 0.6),
                                '&:hover': { color: theme.palette.primary.main },
                              },
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                )}
              </Stack>
            </>
          )}

          {/* ─── Concession ───────────────────────────────────────────── */}
          {surface(
            <>
              {sectionHeader('Concession')}

              <Stack spacing={2}>
                <TextField
                  label="Label"
                  placeholder="e.g. Merit Scholarship 2026-27"
                  value={label}
                  onChange={e => setLabel(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={saving}
                  required
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <FormControl sx={{ minWidth: 240 }} size="small" disabled={saving}>
                    <InputLabel>Mode</InputLabel>
                    <Select
                      label="Mode"
                      value={mode}
                      onChange={e => setMode(e.target.value as ConcessionMode)}
                    >
                      <MenuItem value="percent">Percent — % off each line</MenuItem>
                      <MenuItem value="flat">Flat — fixed amount off the bill</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label={isPercent ? 'Percent' : 'Amount'}
                    type="number"
                    size="small"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    inputProps={{ min: 0, step: isPercent ? 0.1 : 100, max: isPercent ? 100 : undefined }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{isPercent ? '%' : currency}</InputAdornment>,
                    }}
                    sx={{ flex: 1 }}
                    disabled={saving}
                    required
                  />
                </Stack>

                {effectText && (
                  <Alert
                    severity="info"
                    variant="outlined"
                    icon={false}
                    sx={{
                      py: 0.5, borderRadius: 1.5,
                      borderColor: alpha(theme.palette.info.main, 0.2),
                      bgcolor: alpha(theme.palette.info.main, 0.04),
                      '& .MuiAlert-message': { fontSize: '0.78rem', lineHeight: 1.5 },
                    }}
                  >
                    {effectText}
                  </Alert>
                )}

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                    Applies to
                  </Typography>
                  <RadioGroup
                    row
                    value={appliesTo}
                    onChange={(_, v) => setAppliesTo(v as ConcessionAppliesTo)}
                  >
                    <FormControlLabel value="all" control={<Radio size="small" />} label={<Typography variant="body2">Every fee category</Typography>} />
                    <FormControlLabel value="categories" control={<Radio size="small" />} label={<Typography variant="body2">Specific categories</Typography>} />
                  </RadioGroup>
                  {appliesTo === 'categories' && (
                    <Autocomplete
                      multiple
                      size="small"
                      options={categories}
                      getOptionLabel={c => c.name}
                      isOptionEqualToValue={(a, b) => a._id === b._id}
                      value={categories.filter(c => feeCategoryIds.includes(c._id))}
                      onChange={(_, next) => setFeeCategoryIds(next.map(c => c._id))}
                      disabled={saving}
                      renderTags={(value, getTagProps) =>
                        value.map((opt, i) => (
                          <Chip
                            {...getTagProps({ index: i })}
                            key={opt._id}
                            label={opt.name}
                            size="small"
                            sx={{
                              height: 22, fontSize: '0.72rem', fontWeight: 600,
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: theme.palette.primary.dark,
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Pick one or more categories" />
                      )}
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>

                <Divider />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    label="Valid from"
                    type="date"
                    size="small"
                    value={validFrom}
                    onChange={e => setValidFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                    disabled={saving}
                    required
                  />
                  <TextField
                    label="Valid to (optional)"
                    type="date"
                    size="small"
                    value={validTo}
                    onChange={e => setValidTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave blank for evergreen (until revoked)."
                    sx={{ flex: 1 }}
                    disabled={saving}
                  />
                </Stack>

                <TextField
                  label="Reason"
                  placeholder="Optional — internal audit note"
                  size="small"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  disabled={saving}
                />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${softBorder}` }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={save}
          disabled={!canSave || saving}
          sx={{ borderRadius: 1.5, px: 2.5, minWidth: 180, fontWeight: 700 }}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConcessionFormDialog;
